import type { ComponentType } from "react";
import { ReactNode, useState, useEffect, useRef } from "react";
import { Link, useLocation, Redirect } from "wouter";
import { Camera, Image as ImageIcon, Settings, LogOut, Loader2, Menu, X, Grid3X3, ArrowUpFromDot, Wand2, CreditCard, Bug, UserCircle2, Bell, Users, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { prefetchByHref, prefetchCommonRoutesOnIdle } from "@/lib/route-prefetch";
import { useLogout, useListUserNotifications, useMarkAllUserNotificationsRead, useMarkUserNotificationRead, getListUserNotificationsQueryKey } from "@workspace/api-client-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { User as UserIcon } from "lucide-react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { ReportBugDialog } from "@/components/ReportBugDialog";
import { LegalConsentGate } from "@/components/LegalConsentGate";
import { ResponsibleUseGate } from "@/components/ResponsibleUseGate";
import { CreditUsageCounter } from "@/components/credit-usage-counter";

const PRODUCT_LIBRARY_KEY = "aishootlab_last_product_library_path";
const INFLUENCER_MGMT_KEY = "aishootlab_last_influencer_mgmt_path";

function isProductLibraryPath(path: string): boolean {
  return path === "/products" ||
    path.startsWith("/products/") ||
    path.startsWith("/photoshoots/");
}

function isInfluencerMgmtPath(path: string): boolean {
  return path === "/influencer-management" || path.startsWith("/influencer-management/");
}

type LucideIconComponent = ComponentType<{ size?: number | string; className?: string }>;
type NavTone = "default" | "violet" | "amber";
interface NavItem {
  href: string;
  label: string;
  icon: LucideIconComponent;
  isProductLibrary?: boolean;
  isInfluencerMgmt?: boolean;
  isDashboard?: boolean;
  tone?: NavTone;
}
interface NavSection {
  title?: string;
  tone?: NavTone;
  items: NavItem[];
  beta?: boolean;
}

const TONE_STYLES: Record<NavTone, { active: string; hover: string; dot: string; header: string; frame: string }> = {
  default: {
    active: "bg-primary/10 text-primary",
    hover: "text-muted-foreground hover:bg-secondary hover:text-foreground",
    dot: "",
    header: "text-muted-foreground/70",
    frame: "",
  },
  violet: {
    active: "bg-violet-500/20 text-violet-200",
    hover: "text-muted-foreground hover:bg-violet-500/10 hover:text-violet-300",
    dot: "bg-violet-500",
    header: "text-violet-300",
    frame: "border border-violet-500/40 bg-violet-500/5",
  },
  amber: {
    active: "bg-amber-500/20 text-amber-200",
    hover: "text-muted-foreground hover:bg-amber-500/10 hover:text-amber-300",
    dot: "bg-amber-500",
    header: "text-amber-300",
    frame: "border border-amber-500/40 bg-amber-500/5",
  },
};

function useProductLibraryMemory(location: string) {
  const prevLocation = useRef(location);

  useEffect(() => {
    if (isProductLibraryPath(location)) {
      sessionStorage.setItem(PRODUCT_LIBRARY_KEY, location);
    }
    prevLocation.current = location;
  }, [location]);

  const getProductLibraryHref = (): string => {
    if (isProductLibraryPath(location)) return "/products";
    const saved = sessionStorage.getItem(PRODUCT_LIBRARY_KEY);
    return saved && saved !== "/products" ? saved : "/products";
  };

  return getProductLibraryHref;
}

function useInfluencerMgmtMemory(location: string) {
  useEffect(() => {
    if (isInfluencerMgmtPath(location)) {
      // Capture full path including any ?tab=... query string so returning
      // to Influencer Management restores the exact sub-page and tab.
      const full = location + (typeof window !== "undefined" ? window.location.search : "");
      sessionStorage.setItem(INFLUENCER_MGMT_KEY, full);
    }
  }, [location]);

  const getInfluencerMgmtHref = (): string => {
    if (isInfluencerMgmtPath(location)) return "/influencer-management";
    const saved = sessionStorage.getItem(INFLUENCER_MGMT_KEY);
    return saved && saved !== "/influencer-management" ? saved : "/influencer-management";
  };

  return getInfluencerMgmtHref;
}

export function Layout({ children }: { children: ReactNode }) {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const { mutateAsync: logout } = useLogout();
  const queryClient = useQueryClient();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [reportBugOpen, setReportBugOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [topAccountMenuOpen, setTopAccountMenuOpen] = useState(false);
  const getProductLibraryHref = useProductLibraryMemory(location);
  const _getInfluencerMgmtHref = useInfluencerMgmtMemory(location);

  // Warm the most common route chunks once the authed shell has mounted
  // so the very first sidebar click is instant — even on touch devices
  // where there's no hover event to trigger the per-link prefetch.
  useEffect(() => {
    prefetchCommonRoutesOnIdle();
  }, []);

  const { data: featureFlags } = useQuery<{ upsamplerEnabled?: boolean }>({
    queryKey: ["/api/auth/feature-flags"],
    queryFn: () => fetch(`${import.meta.env.BASE_URL}api/auth/feature-flags`, { credentials: "include" }).then(r => r.json()),
    staleTime: 60_000,
  });

  // Task #734 — defer the admin bug-reports fan-out until after the
  // page is interactive. The badge in the sidebar footer is never
  // critical for first paint, and the API call was contending with
  // the Plan Content posts page on every layout mount for admin users.
  const [bugReportsEnabled, setBugReportsEnabled] = useState(false);
  useEffect(() => {
    if (!user?.isAdmin) return;
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
    };
    if (typeof w.requestIdleCallback === "function") {
      const handle = w.requestIdleCallback(() => setBugReportsEnabled(true), { timeout: 2000 });
      return () => {
        const cancel = (window as Window & { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback;
        if (typeof cancel === "function") cancel(handle);
      };
    }
    const t = window.setTimeout(() => setBugReportsEnabled(true), 1500);
    return () => window.clearTimeout(t);
  }, [user?.isAdmin]);
  const { data: openBugReports } = useQuery<Array<{ id: number }>>({
    queryKey: ["/api/bug-reports", "open"],
    queryFn: () => fetch(`${import.meta.env.BASE_URL}api/bug-reports?status=open`, { credentials: "include" }).then(r => r.json()),
    enabled: !!user?.isAdmin && bugReportsEnabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const openBugCount = Array.isArray(openBugReports) ? openBugReports.length : 0;

  const handleLogout = async () => {
    try {
      await logout();
    } catch { /* ignore */ }
    queryClient.clear();
    // Hard-navigate to the landing page so all in-memory React state
    // (auth context, cached queries, route guards) is rebuilt from
    // scratch. setLocation("/") alone leaves the dashboard shell on
    // screen until the auth query refetches.
    window.location.assign("/");
  };

  const upsamplerEnabled = featureFlags?.upsamplerEnabled === true;

  const productLibraryHref = getProductLibraryHref();

  const canSeeNotifications = !!user?.modelManagementAccess;
  const { data: notifications } = useListUserNotifications({
    query: {
      enabled: canSeeNotifications,
      refetchInterval: 60_000,
    },
  });
  const { mutateAsync: markRead } = useMarkUserNotificationRead();
  const { mutateAsync: markAllRead } = useMarkAllUserNotificationsRead();
  // Defensive: only treat the response as a list when it actually is
  // one. If the first /notifications request right after login resolves
  // to a non-array (e.g. an error envelope), `(notifications || []).filter`
  // would still throw because `|| []` only catches null/undefined. That
  // throw would render the global "Something went wrong" page.
  const unread = Array.isArray(notifications) ? notifications.filter((n) => !n.isRead) : [];
  const refreshNotifications = () => {
    queryClient.invalidateQueries({ queryKey: getListUserNotificationsQueryKey() });
  };
  const handleNotificationClick = async (id: number, modelProfileId?: number | null) => {
    try { await markRead({ id }); } catch { /* ignore */ }
    refreshNotifications();
    if (modelProfileId) {
      setLocation(`/model-management?modelId=${modelProfileId}`);
    } else {
      setLocation("/model-management");
    }
  };

  const aiAvatarItems: NavItem[] = [];
  if (user?.modelManagementAccess) {
    aiAvatarItems.push({ href: "/model-management", label: "Create and Manage Avatars and Pets", icon: UserCircle2, tone: "violet" });
  }
  if (user?.influencerManagementEnabled) {
    aiAvatarItems.push({ href: "/influencer-management", label: "Social Media Content Creation with your Avatars", icon: Users, isInfluencerMgmt: true, tone: "violet" });
  }

  const productStudioItems: NavItem[] = [
    { href: productLibraryHref, label: "Product Photoshoots", icon: ImageIcon, isProductLibrary: true, tone: "amber" },
    { href: "/photo-director", label: "Photo Director Library", icon: Camera, tone: "amber" },
  ];

  const upsamplerItems: NavItem[] = [];
  if ((user?.hasUpsamplerAccess || user?.isAdmin) && upsamplerEnabled) {
    upsamplerItems.push({ href: "/upsampler", label: "Upsampler", icon: ArrowUpFromDot });
  }

  const navSections: NavSection[] = [
    {
      items: [
        { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard, isDashboard: true },
        { href: "/edit-image", label: "Create or Edit an Image", icon: Wand2 },
      ],
    },
  ];
  if (aiAvatarItems.length) navSections.push({ title: "AI Avatar Studio", tone: "violet", items: aiAvatarItems });
  navSections.push({ title: "Product Studio", tone: "amber", items: productStudioItems, beta: true });
  if (upsamplerItems.length) navSections.push({ items: upsamplerItems });

  const renderNavItem = (item: NavItem, opts: { onClick?: () => void } = {}) => {
    const isActive = item.isProductLibrary
      ? isProductLibraryPath(location)
      : item.isInfluencerMgmt
        ? isInfluencerMgmtPath(location)
        : item.isDashboard
          ? location === "/" || location === "/dashboard"
          : location.startsWith(item.href);
    const tone = TONE_STYLES[item.tone ?? "default"];
    return (
      <Link
        key={item.label}
        href={item.href}
        onClick={opts.onClick}
        onMouseEnter={() => prefetchByHref(item.href)}
        onFocus={() => prefetchByHref(item.href)}
        onTouchStart={() => prefetchByHref(item.href)}
        className={cn(
          "flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200",
          isActive ? tone.active : tone.hover
        )}
        aria-current={isActive ? "page" : undefined}
      >
        <item.icon size={20} />
        <span className="text-sm">{item.label}</span>
      </Link>
    );
  };

  const renderSectionHeader = (section: NavSection) => {
    if (!section.title) return null;
    const tone = TONE_STYLES[section.tone ?? "default"];
    return (
      <div className={cn("flex items-center gap-2 px-3 pt-1 pb-2 text-sm font-semibold uppercase tracking-wider", tone.header)}>
        {tone.dot && <span className={cn("inline-block w-2 h-2 rounded-full", tone.dot)} />}
        {section.title}
        {section.beta && (
          <span
            className="ml-1 rounded-sm border border-current px-1 py-px text-[9px] font-semibold leading-none tracking-wider opacity-70"
            title="Beta — in active development"
          >
            BETA
          </span>
        )}
      </div>
    );
  };

  const sectionFrameClass = (section: NavSection) => {
    if (!section.title) return "space-y-1";
    const tone = TONE_STYLES[section.tone ?? "default"];
    return cn("space-y-1 rounded-2xl p-2", tone.frame);
  };

  // Renders the shared Profile / Billing / Log out menu items used in all
  // three account popovers (top-right banner, bottom-left desktop sidebar,
  // and mobile drawer bottom). Pass `close` to dismiss the popover after
  // selection.
  const renderAccountMenuItems = (close: () => void) => (
    <>
      <button
        type="button"
        role="menuitem"
        onClick={() => { close(); setLocation("/profile"); }}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors text-left",
          location.startsWith("/profile")
            ? "bg-primary/15 text-primary"
            : "hover:bg-secondary focus:bg-secondary focus:outline-none"
        )}
      >
        <UserIcon size={16} />
        Profile
      </button>
      <button
        type="button"
        role="menuitem"
        onClick={() => { close(); setLocation("/billing"); }}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors text-left",
          location.startsWith("/billing")
            ? "bg-primary/15 text-primary"
            : "hover:bg-secondary focus:bg-secondary focus:outline-none"
        )}
      >
        <CreditCard size={16} />
        Billing
      </button>
      {user?.isAdmin && (
        <>
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            role="menuitem"
            onClick={() => { close(); setLocation("/admin"); }}
            className={cn(
              "flex w-full items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors text-left",
              location.startsWith("/admin")
                ? "bg-primary/15 text-primary"
                : "hover:bg-secondary focus:bg-secondary focus:outline-none"
            )}
          >
            <Settings size={16} />
            Admin Panel
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => { close(); setLocation("/reported-bugs"); }}
            className={cn(
              "flex w-full items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors text-left",
              location.startsWith("/reported-bugs")
                ? "bg-primary/15 text-primary"
                : "hover:bg-secondary focus:bg-secondary focus:outline-none"
            )}
          >
            <Bug size={16} />
            <span className="flex-1">Reported Bugs</span>
            {openBugCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold"
                data-testid="badge-open-bug-count"
              >
                {openBugCount > 99 ? "99+" : openBugCount}
              </span>
            )}
          </button>
        </>
      )}
      <div className="my-1 border-t border-border" />
      <button
        type="button"
        role="menuitem"
        onClick={() => { close(); handleLogout(); }}
        className="flex w-full items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors text-left text-destructive hover:bg-destructive/10 focus:bg-destructive/10 focus:outline-none"
      >
        <LogOut size={16} />
        Log out
      </button>
    </>
  );

  // Top-right profile button + dropdown shared between desktop banner
  // and the mobile banner. Avatar always visible; name visible md+.
  const TopBannerProfile = () => (
    <Popover open={topAccountMenuOpen} onOpenChange={setTopAccountMenuOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseEnter={() => { prefetchByHref("/profile"); prefetchByHref("/billing"); }}
          onFocus={() => { prefetchByHref("/profile"); prefetchByHref("/billing"); }}
          onTouchStart={() => { prefetchByHref("/profile"); prefetchByHref("/billing"); }}
          className="inline-flex items-center gap-2 min-h-[44px] min-w-[44px] px-1.5 sm:px-2 py-1 rounded-full hover:bg-secondary transition-colors"
          aria-label="Open account menu"
          aria-haspopup="menu"
          aria-expanded={topAccountMenuOpen}
          data-testid="banner-profile-button"
        >
          <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-muted-foreground font-bold">
            {user?.name?.charAt(0).toUpperCase() || "U"}
          </div>
          <span className="hidden sm:inline text-sm font-semibold max-w-[140px] truncate">
            {user?.name}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" className="w-56 p-1" role="menu">
        {renderAccountMenuItems(() => setTopAccountMenuOpen(false))}
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Top Banner — sticky on every authenticated page. Houses the brand
          logo (left), a flexible centre slot (reserved for future items),
          and the Image Library shortcut + profile menu (right). On mobile
          we still surface the hamburger and notification bell here so the
          banner remains the single top-of-app surface. */}
      <header
        className="sticky top-0 z-50 bg-card border-b border-border h-14 md:h-16 flex items-center justify-between gap-2 px-3 md:px-6"
        data-testid="app-top-banner"
      >
        <div className="flex items-center gap-2 min-w-0">
          {/* Mobile-only hamburger to open the sidebar drawer */}
          <button
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-md hover:bg-secondary"
            aria-label={mobileNavOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={mobileNavOpen}
          >
            {mobileNavOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <Link
            href="/dashboard"
            className="flex items-center gap-2 md:gap-3 min-w-0 hover:opacity-90 transition-opacity"
            aria-label="Go to dashboard"
          >
            <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground shrink-0">
              <Camera size={14} className="md:hidden" />
              <Camera size={18} className="hidden md:inline" />
            </div>
            <span className="font-display font-bold text-xs leading-tight sm:text-lg sm:leading-normal md:text-xl tracking-tight">
              <span className="sm:hidden">
                AI Shoot
                <br />
                Lab
              </span>
              <span className="hidden sm:inline">AI Shoot Lab</span>
            </span>
          </Link>
        </div>

        {/* Centre slot — intentionally empty. Reserved for future banner
            items (search, global filters, etc.) without restructuring. */}
        <div className="flex-1" aria-hidden="true" />

        <div className="flex items-center gap-2 md:gap-4">
          {canSeeNotifications && (
            <NotificationBell
              unread={unread}
              onSelect={handleNotificationClick}
              onMarkAll={async () => { await markAllRead(); refreshNotifications(); }}
            />
          )}
          <Link
            href="/assets"
            onClick={() => {
              try {
                const raw = sessionStorage.getItem("assetLibraryPage");
                const parsed = raw ? JSON.parse(raw) : {};
                sessionStorage.setItem("assetLibraryPage", JSON.stringify({ ...parsed, page: 1 }));
              } catch { /* ignore */ }
            }}
            onMouseEnter={() => prefetchByHref("/assets")}
            onFocus={() => prefetchByHref("/assets")}
            onTouchStart={() => prefetchByHref("/assets")}
            className="inline-flex items-center justify-center gap-2 h-11 min-w-[44px] px-3 sm:px-4 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 active:scale-95 transition-all"
            aria-label="Image Library"
            title="Image Library"
            data-testid="banner-image-library"
          >
            <Grid3X3 size={18} />
            <span className="hidden sm:inline">Image Library</span>
          </Link>
          <button
            type="button"
            onClick={() => setReportBugOpen(true)}
            className="inline-flex items-center justify-center gap-2 h-11 min-w-[44px] px-3 sm:px-4 rounded-lg border border-green-500/40 text-green-500 text-sm font-semibold hover:bg-green-500/10 active:scale-95 transition-all"
            aria-label="Report a Bug"
            title="Report a Bug"
            data-testid="banner-report-bug"
          >
            <Bug size={18} />
            <span className="hidden sm:inline">Report a Bug</span>
          </button>
          <TopBannerProfile />
        </div>
      </header>

      <div className="flex flex-1 min-h-0">

      {/* Mobile slide-in drawer (left), with backdrop. Always rendered so the
          slide animation has something to interpolate; visibility is gated on
          `mobileNavOpen`. */}
      <div
        className={cn(
          "md:hidden fixed inset-0 z-40 transition-opacity duration-200",
          mobileNavOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setMobileNavOpen(false)}
        aria-hidden={!mobileNavOpen}
      >
        <div className="absolute inset-0 bg-black/50" />
        <aside
          className={cn(
            "absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-card border-r border-border shadow-2xl flex flex-col",
            "transform transition-transform duration-200 ease-out",
            mobileNavOpen ? "translate-x-0" : "-translate-x-full"
          )}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Main navigation"
        >
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <span className="text-sm font-semibold text-muted-foreground">Menu</span>
            <button
              onClick={() => setMobileNavOpen(false)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-md hover:bg-secondary"
              aria-label="Close navigation"
            >
              <X size={22} />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto p-3 space-y-1">
            {navSections.map((section, idx) => (
              <div key={section.title ?? `section-${idx}`} className={sectionFrameClass(section)}>
                {renderSectionHeader(section)}
                {section.items.map((item) => renderNavItem(item, { onClick: () => setMobileNavOpen(false) }))}
              </div>
            ))}
          </nav>
          <div className="p-3 border-t border-border">
            {/* Compact mobile footer: credit usage on the left, Buy More
                Credits CTA on the right. The user's avatar/email and
                Report a Bug were removed here — both are reachable from
                the top-right of the header on mobile. */}
            <div className="flex items-center gap-2">
              <CreditUsageCounter variant="mobile-compact" />
              <Link
                href="/billing"
                onClick={() => setMobileNavOpen(false)}
                className="shrink-0 inline-flex items-center justify-center px-3 py-2 rounded-lg text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors whitespace-nowrap"
                data-testid="buy-more-credits-button-mobile"
                aria-label="Buy more credits"
              >
                Buy More
              </Link>
            </div>
          </div>
        </aside>
      </div>

      {/* Sidebar — logo lives in the top banner now, so the sidebar starts
          directly with the nav. Stays sticky just below the sticky banner. */}
      <aside className="w-64 bg-card border-r border-border flex-col hidden md:flex sticky top-16 h-[calc(100vh-4rem)]">
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navSections.map((section, idx) => (
            <div key={section.title ?? `section-${idx}`} className={sectionFrameClass(section)}>
              {renderSectionHeader(section)}
              {section.items.map((item) => renderNavItem(item))}
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="mb-2 space-y-2">
            <CreditUsageCounter variant="sidebar" />
            {/* "Buy More Credits" CTA below the usage counter so the
                full credit count stays on one line. Routes to the
                Billing page where Booster Packs are surfaced first. */}
            <Link
              href="/billing"
              className="flex w-full items-center justify-center px-3 py-2 rounded-xl text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              data-testid="buy-more-credits-button"
              aria-label="Buy more credits"
            >
              Buy More Credits
            </Link>
          </div>
          <Popover open={accountMenuOpen} onOpenChange={setAccountMenuOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                onMouseEnter={() => { prefetchByHref("/profile"); prefetchByHref("/billing"); }}
                onFocus={() => { prefetchByHref("/profile"); prefetchByHref("/billing"); }}
                onTouchStart={() => { prefetchByHref("/profile"); prefetchByHref("/billing"); }}
                className={cn(
                  "flex w-full items-center gap-3 px-4 py-3 rounded-xl transition-colors cursor-pointer text-left",
                  (location.startsWith("/profile") || location.startsWith("/billing"))
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-secondary"
                )}
                aria-label="Open account menu"
                aria-haspopup="menu"
                aria-expanded={accountMenuOpen}
              >
                <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-muted-foreground font-bold">
                  {user?.name?.charAt(0).toUpperCase() || "U"}
                </div>
                <div className="flex-1 overflow-hidden">
                  <p className="text-sm font-semibold truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" align="start" className="w-56 p-1" role="menu">
              {renderAccountMenuItems(() => setAccountMenuOpen(false))}
            </PopoverContent>
          </Popover>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* App-wide past_due banner — visible on every authenticated
            page so the user is always one click from updating billing. */}
        {!user?.isAdmin && user?.subscriptionStatus === "past_due" && (
          <div
            className="bg-amber-50 border-b border-amber-300 text-amber-900 px-4 py-2 flex items-center justify-between gap-3 text-sm"
            data-testid="banner-past-due"
          >
            <span>
              Your last payment didn't go through. Update your billing details to keep automatic posting and analytics running.
            </span>
            <Link href="/billing">
              <button className="px-3 py-1 rounded-md bg-amber-900 text-amber-50 text-xs font-medium hover:bg-amber-800">
                Update billing
              </button>
            </Link>
          </div>
        )}
        {/* Task #356 — app-wide pending-cancellation banner. Self-renders
            from /billing/summary (same query key the billing page uses,
            so React Query dedupes the fetch). Hidden for admins and any
            non-paying account. */}
        {!user?.isAdmin && <CancelAtPeriodEndBanner />}
        {children}
      </main>
      </div>

      <ReportBugDialog open={reportBugOpen} onOpenChange={setReportBugOpen} />
      <LegalConsentGate />
      <ResponsibleUseGate />
    </div>
  );
}

type NotificationItem = { id: number; title: string; body?: string | null; modelProfileId?: number | null; createdAt: string };
function NotificationBell({ unread, onSelect, onMarkAll }: {
  unread: NotificationItem[];
  onSelect: (id: number, modelProfileId?: number | null) => void;
  onMarkAll: () => void | Promise<void>;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="relative inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-secondary transition-colors"
          aria-label="Notifications"
        >
          <Bell size={18} />
          {unread.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
              {unread.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <span className="text-sm font-semibold">Notifications</span>
          {unread.length > 0 && (
            <button onClick={() => void onMarkAll()} className="text-xs text-primary hover:underline">Mark all read</button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {unread.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">You're all caught up.</div>
          ) : (
            unread.map((n) => (
              <button
                key={n.id}
                onClick={() => onSelect(n.id, n.modelProfileId)}
                className="w-full text-left p-3 hover:bg-secondary border-b border-border last:border-b-0"
              >
                <div className="text-sm font-medium">{n.title}</div>
                {n.body && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</div>}
                <div className="text-[10px] text-muted-foreground mt-1">{new Date(n.createdAt).toLocaleString()}</div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ProtectedRoute({ component: Component, adminOnly = false, params = {} }: { component: React.ComponentType<Record<string, string | undefined>>, adminOnly?: boolean, params?: Record<string, string | undefined> }) {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve the destination (path + query + hash) so the login page
    // can send the user back here after they sign in. Critical for flows
    // that return from third-party sites with state in the URL — e.g.
    // Stripe Checkout's success_url puts ?pack_session_id=… on /billing
    // and we need that param after re-auth so the credit grant can be
    // confirmed by the polling effect.
    const here = window.location.pathname + window.location.search + window.location.hash;
    const next = here && here !== "/login" ? `?next=${encodeURIComponent(here)}` : "";
    return <Redirect to={`/login${next}`} />;
  }

  // Subscription-status routing for every authenticated page load.
  // Admins always pass; pending_* / cancelled get redirected to the
  // appropriate funnel step. past_due passes (in-app banner pattern).
  // Gate by lifecycle status only — `emailVerified` alone is not a
  // gating signal because legacy/admin-granted accounts may have it
  // false but are otherwise fully entitled.
  if (!user?.isAdmin) {
    const status = user?.subscriptionStatus;
    const here = window.location.pathname;
    if (status === "pending_verification" && !here.endsWith("/verify-email-pending")) {
      return <Redirect to="/verify-email-pending" />;
    }
    if (status === "pending_payment" && !here.endsWith("/checkout")) {
      return <Redirect to="/checkout" />;
    }
    // Cancelled users are funnelled to /billing but must be able to
    // actually render that page — skip the redirect when they're
    // already there (and let /billing be reachable as well).
    if (status === "cancelled" && !here.endsWith("/billing")) {
      return <Redirect to="/billing" />;
    }
  }

  if (adminOnly && !user?.isAdmin) {
    return (
      <Layout>
        <div className="p-12 text-center">
          <h2 className="text-2xl font-bold text-destructive">Access Denied</h2>
          <p className="text-muted-foreground mt-2">You need administrator privileges to view this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Component {...params} />
    </Layout>
  );
}

// Task #356 — App-wide "subscription set to cancel" banner.
//
// Reads /billing/summary (same query key as the billing page + the
// credit usage counter, so React Query dedupes the request to one
// fetch per cache window). Only renders when Stripe has flagged the
// subscription as cancel_at_period_end AND the user can self-serve
// manage it — complimentary accounts, admin accounts, and users with
// no Stripe customer are filtered out server-side via
// canManageSubscription, so the banner never offers a Resume button
// the user can't action.
function CancelAtPeriodEndBanner() {
  const [opening, setOpening] = useState(false);
  type Summary = {
    cancelAtPeriodEnd?: boolean;
    cancelAt?: string | null;
    canManageSubscription?: boolean;
  };
  const { data } = useQuery<Summary>({
    queryKey: ["billing", "summary"],
    queryFn: async () => {
      const res = await fetch(`${import.meta.env.BASE_URL}api/billing/summary`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load billing");
      return res.json();
    },
    staleTime: 60_000,
  });
  if (!data?.cancelAtPeriodEnd || !data?.canManageSubscription) return null;
  const dateStr = data.cancelAt ? new Date(data.cancelAt).toLocaleDateString() : null;
  const openPortal = async () => {
    setOpening(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/billing/portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body?.url) {
        window.location.assign(body.url);
        return;
      }
    } catch {
      // Fall through and re-enable the button so the user can retry.
    }
    setOpening(false);
  };
  return (
    <div
      className="bg-amber-50 border-b border-amber-300 text-amber-900 px-4 py-2 flex items-center justify-between gap-3 text-sm"
      data-testid="banner-cancel-at-period-end-global"
    >
      <span>
        Your plan {dateStr ? <>cancels on <strong>{dateStr}</strong></> : <>is set to cancel at the end of the current billing cycle</>}. You'll keep access until then.
      </span>
      <button
        onClick={openPortal}
        disabled={opening}
        className="px-3 py-1 rounded-md bg-amber-900 text-amber-50 text-xs font-medium hover:bg-amber-800 disabled:opacity-60"
        data-testid="button-resume-subscription-global"
      >
        {opening ? "Opening…" : "Resume subscription"}
      </button>
    </div>
  );
}
